// Ingest source resolver (§3.2/§10.1). Classifies a source path into text or
// video and enforces the strict input allowlist. PDF/JSON/XLSX are explicitly
// unsupported here (they belong to other commands), returning a clear error.

import * as fs from "node:fs";
import * as path from "node:path";

export const TEXT_EXTENSIONS = [".txt", ".md", ".docx"] as const;

// Common Gemini-ingestible video containers (§10.1); extension → MIME.
export const VIDEO_MIME: Readonly<Record<string, string>> = {
  ".mp4": "video/mp4",
  ".mov": "video/mov",
  ".avi": "video/avi",
  ".mpeg": "video/mpeg",
  ".mpg": "video/mpg",
  ".webm": "video/webm",
  ".flv": "video/x-flv",
  ".wmv": "video/wmv",
};

export type SourceKind = "text" | "video-file" | "video-directory";

export class UnsupportedInputError extends Error {
  constructor(readonly received: string) {
    super(`Unsupported input: ${received}`);
  }
}

function ext(p: string): string {
  return path.extname(p).toLowerCase();
}

function isVideoExt(e: string): boolean {
  return Object.hasOwn(VIDEO_MIME, e);
}

export type ResolvedSource =
  | { kind: "text"; path: string; ext: string }
  | { kind: "video-file"; path: string; ext: string }
  | { kind: "video-directory"; path: string; files: string[] };

export function resolveSource(sourcePath: string): ResolvedSource {
  const stat = fs.statSync(sourcePath);
  if (stat.isDirectory()) {
    // Directory input is only for a set of video episodes (§10.1); scan one level.
    const entries = fs.readdirSync(sourcePath)
      .filter((name) => !name.startsWith("."))
      .filter((name) => isVideoExt(ext(name)))
      .sort()
      .map((name) => path.join(sourcePath, name));
    if (entries.length === 0) throw new UnsupportedInputError(`directory has no supported video files: ${sourcePath}`);
    return { kind: "video-directory", path: sourcePath, files: entries };
  }
  const e = ext(sourcePath);
  if ((TEXT_EXTENSIONS as readonly string[]).includes(e)) return { kind: "text", path: sourcePath, ext: e };
  if (isVideoExt(e)) return { kind: "video-file", path: sourcePath, ext: e };
  throw new UnsupportedInputError(`file extension ${e || "(none)"} is not supported by ingest (txt/md/docx/video only)`);
}
